import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Button, Space, Modal, Form, Select, Input, message, Typography } from 'antd';
import { FormOutlined, ReloadOutlined } from '@ant-design/icons';
import MainLayout from '../layout/layout';
import { apiClient } from '../../config/apiClient';

const { Text } = Typography;

const colorByEstado = {
  PENDIENTE: 'gold',
  APROBADA: 'green',
  RECHAZADA: 'red',
};

const normalizarTelefonoWhatsapp = (telefono) => {
  const digits = String(telefono || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('54')) return digits;
  if (digits.length === 10) return `549${digits}`;
  return digits;
};

const SolicitudesAlumnos = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [solicitudActual, setSolicitudActual] = useState(null);
  const [form] = Form.useForm();

  const loadSolicitudes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/solicitudes-alumnos');
      if (!response.ok) {
        const err = await response.json();
        message.error(err.message || 'Error al cargar solicitudes');
        return;
      }
      const json = await response.json();
      setData(Array.isArray(json) ? json : []);
    } catch (error) {
      message.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSolicitudes();
  }, []);

  const abrirGestion = (sol) => {
    setSolicitudActual(sol);
    form.setFieldsValue({
      estado: sol.sol_estado,
      observaciones: sol.sol_observaciones || '',
    });
    setModalOpen(true);
  };

  const guardarGestion = async (values) => {
    if (!solicitudActual?.sol_id) return;
    setSaving(true);
    try {
      const response = await apiClient.put(`/solicitudes-alumnos/${solicitudActual.sol_id}`, {
        estado: values.estado,
        observaciones: values.observaciones || null,
      });
      if (!response.ok) {
        const err = await response.json();
        message.error(err.message || 'Error al actualizar la solicitud');
        return;
      }
      message.success('Solicitud actualizada correctamente');
      setModalOpen(false);
      setSolicitudActual(null);
      form.resetFields();
      loadSolicitudes();
    } catch (error) {
      message.error('Error al actualizar la solicitud');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'sol_fecha_creacion',
      key: 'sol_fecha_creacion',
      width: 140,
      render: (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString('es-AR');
      },
    },
    {
      title: 'Alumno',
      key: 'alumno',
      render: (_, row) => `${row.sol_nombre || ''} ${row.sol_apellido || ''}`.trim() || '-',
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria_descripcion',
      key: 'categoria_descripcion',
      width: 160,
      render: (value) => value || '-',
    },
    {
      title: 'Contacto',
      key: 'contacto',
      render: (_, row) => (
        <Text style={{ color: '#f5f5f5' }}>{row.sol_email || '-'}</Text>
      ),
    },
    {
      title: 'Teléfono',
      key: 'telefono',
      width: 180,
      render: (_, row) => {
        if (!row.sol_telefono) return '-';
        const telWa = normalizarTelefonoWhatsapp(row.sol_telefono);
        return (
          <a
            href={`https://wa.me/${telWa}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#d8ff3e' }}
          >
            {row.sol_telefono}
          </a>
        );
      },
    },
    {
      title: 'Estado',
      dataIndex: 'sol_estado',
      key: 'sol_estado',
      width: 130,
      render: (value) => <Tag color={colorByEstado[value] || 'default'}>{value || 'PENDIENTE'}</Tag>,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 140,
      render: (_, row) => (
        <Button size="small" onClick={() => abrirGestion(row)}>
          Gestionar
        </Button>
      ),
    },
  ];

  return (
    <MainLayout>
      <Card
        title={(
          <span>
            <FormOutlined style={{ marginRight: 8 }} />
            Solicitud de alumnos
          </span>
        )}
        extra={(
          <Button icon={<ReloadOutlined />} onClick={loadSolicitudes} loading={loading}>
            Actualizar
          </Button>
        )}
      >
        <Table
          rowKey="sol_id"
          loading={loading}
          columns={columns}
          dataSource={data}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        open={modalOpen}
        title="Gestionar solicitud"
        onCancel={() => {
          setModalOpen(false);
          setSolicitudActual(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText="Guardar"
      >
        <Form form={form} layout="vertical" onFinish={guardarGestion}>
          <Form.Item name="estado" label="Estado" rules={[{ required: true, message: 'Seleccioná un estado' }]}>
            <Select
              options={[
                { value: 'PENDIENTE', label: 'Pendiente' },
                { value: 'APROBADA', label: 'Aprobada' },
                { value: 'RECHAZADA', label: 'Rechazada' },
              ]}
            />
          </Form.Item>
          <Form.Item name="observaciones" label="Observaciones">
            <Input.TextArea rows={4} placeholder="Opcional" />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default SolicitudesAlumnos;
